import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import type { SLBPosition, NumericFilterCondition, LocalInventoryFilters } from '@/types';
import type { ContextMenuItem } from '@/components/ui/ContextMenu';
import { formatCellValue, COLUMN_ID_TO_FILTER_ID, CONDITION_KEYS, COLUMN_ID_TO_FILTER_ARRAY_KEY } from '@/utils/inventory';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  EyeSlashIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  TableCellsIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { PinLeftIcon, PinRightIcon } from '@/components/ui/icons/PinIcons';
import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';
import type { ColumnConfig, ColumnPinSide } from '@/stores/inventory/inventory-column-store';
import { useUIStore } from '@/stores/ui/ui-store';
import { useToastStore } from '@/stores/toast-store';
import { buildSelectionText, copyTsvToClipboard, exportSelectionToCsv, makeCsvFilename } from '@/utils/cell-selection-utils';
import { MOD_KEY, MOD_ALT_KEY } from '@/utils/platform';

type InventoryColumnDef = ColumnDef<SLBPosition, unknown>;

interface UseInventoryContextMenusParams {
  columnConfig: ColumnConfig[];
  columns: InventoryColumnDef[];
  sorting: { id: string; desc: boolean }[];
  setSorting: (sorting: { id: string; desc: boolean }[]) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  toggleColumnVisibility: (columnId: string) => void;
  handleFilterColumnClick: (columnId: string) => void;
  localInventoryFilters: LocalInventoryFilters;
  setLocalInventoryFilters: (filters: LocalInventoryFilters) => void;
  sortedDataRef: React.MutableRefObject<SLBPosition[]>;
  /** Column IDs in visual order (from computeVisualColIds) */
  colIds: string[];
  /** External selection state from useCellSelection */
  selectedCellsForCopy: () => Set<string>;
  selectedCellCount: () => number;
  selectColumn: (colIndex: number, additive: boolean) => void;
  deselectColumn: (colIndex: number) => void;
  isColumnFullySelected: (colIndex: number) => boolean;
  colIdToIndex: Map<string, number>;
}

export function useInventoryContextMenus({
  columnConfig,
  columns,
  sorting,
  setSorting,
  getPinState,
  pinColumn,
  toggleColumnVisibility,
  handleFilterColumnClick,
  localInventoryFilters,
  setLocalInventoryFilters,
  sortedDataRef,
  colIds,
  selectedCellsForCopy,
  selectedCellCount,
  selectColumn,
  deselectColumn,
  isColumnFullySelected,
  colIdToIndex,
}: UseInventoryContextMenusParams) {
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((s) => s.setColumnsTabActive);
  const setFilterSidebarCollapsed = useUIStore((s) => s.setFilterSidebarCollapsed);
  const addToast = useToastStore((s) => s.addToast);
  const setSidebarHighlightColumnId = useUIStore((s) => s.setSidebarHighlightColumnId);

  /** Switch sidebar to Columns tab and briefly highlight the column row. */
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const flashColumnInSidebar = useCallback((columnId: string) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setFilterSidebarCollapsed(false);
    setSidebarActiveTab('columns');
    setColumnsTabActive(true);
    setSidebarHighlightColumnId(columnId);
    highlightTimerRef.current = setTimeout(() => setSidebarHighlightColumnId(null), 2000);
  }, [setFilterSidebarCollapsed, setSidebarActiveTab, setColumnsTabActive, setSidebarHighlightColumnId]);

  // Header context menu
  const [headerCtxMenu, setHeaderCtxMenu] = useState<{ columnId: string; x: number; y: number } | null>(null);
  const handleColumnContextMenu = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setHeaderCtxMenu({ columnId, x: e.clientX, y: e.clientY });
    },
    []
  );
  const closeHeaderCtxMenu = useCallback(() => setHeaderCtxMenu(null), []);

  const headerCtxMenuItems = useMemo((): ContextMenuItem[] => {
    if (!headerCtxMenu) return [];
    const colId = headerCtxMenu.columnId;
    const colConfig = columnConfig.find((c) => c.id === colId);
    const label = colConfig?.label ?? colId;
    const currentSort = sorting.find((s) => s.id === colId);
    const filterId = COLUMN_ID_TO_FILTER_ID[colId];
    const isFilterable = !!filterId;
    const pin = getPinState(colId);
    const items: ContextMenuItem[] = [
      {
        label: 'Sort ascending',
        icon: <ArrowUpIcon className="h-3.5 w-3.5" />,
        checked: !!currentSort && !currentSort.desc,
        onClick: () => setSorting([{ id: colId, desc: false }]),
      },
      {
        label: 'Sort descending',
        icon: <ArrowDownIcon className="h-3.5 w-3.5" />,
        checked: !!currentSort && currentSort.desc,
        onClick: () => setSorting([{ id: colId, desc: true }]),
      },
      ...(currentSort ? [{
        label: 'Clear sort',
        icon: <XMarkIcon className="h-3.5 w-3.5" />,
        onClick: () => setSorting(sorting.filter((s) => s.id !== colId)),
      }] : []),
    ];
    if (isFilterable) {
      items.push({
        label: `Filter by ${label}`,
        icon: <FunnelIcon className="h-3.5 w-3.5" />,
        dividerBefore: true,
        onClick: () => handleFilterColumnClick(colId),
      });
    }
    if (colId !== 'rowNumber') {
      items.push(
        {
          label: 'Pin left',
          icon: <PinLeftIcon className="h-3.5 w-3.5" />,
          dividerBefore: !isFilterable,
          checked: pin === 'left',
          onClick: () => { pinColumn(colId, pin === 'left' ? null : 'left'); flashColumnInSidebar(colId); },
        },
        {
          label: 'Pin right',
          icon: <PinRightIcon className="h-3.5 w-3.5" />,
          checked: pin === 'right',
          onClick: () => { pinColumn(colId, pin === 'right' ? null : 'right'); flashColumnInSidebar(colId); },
        },
      );
      if (pin) {
        items.push({
          label: 'Unpin',
          icon: <XMarkIcon className="h-3.5 w-3.5" />,
          onClick: () => { pinColumn(colId, null); flashColumnInSidebar(colId); },
        });
      }
      items.push(
        {
          label: 'Hide column',
          icon: <EyeSlashIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => { toggleColumnVisibility(colId); flashColumnInSidebar(colId); },
        }
      );
    }
    // Select / deselect column
    const ci = colIdToIndex.get(colId);
    if (ci !== undefined) {
      const colFullySelected = isColumnFullySelected(ci);
      if (colFullySelected) {
        items.push({
          label: 'Deselect column',
          icon: <ViewColumnsIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => deselectColumn(ci),
        });
      } else {
        const cellCount = selectedCellCount();
        const additive = cellCount > 1;
        items.push({
          label: additive ? 'Add column to selection' : 'Select column',
          icon: <ViewColumnsIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => selectColumn(ci, additive),
        });
      }
    }
    return items;
  }, [headerCtxMenu, columnConfig, sorting, getPinState, pinColumn, toggleColumnVisibility, handleFilterColumnClick, setSorting, flashColumnInSidebar, colIdToIndex, selectColumn, deselectColumn, isColumnFullySelected, selectedCellCount]);

  // Cell context menu
  const [cellCtxMenu, setCellCtxMenu] = useState<{ columnId: string; item: SLBPosition; x: number; y: number } | null>(null);
  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent, columnId: string, item: SLBPosition) => {
      e.preventDefault();
      e.stopPropagation();
      setCellCtxMenu({ columnId, item, x: e.clientX, y: e.clientY });
    },
    []
  );
  const closeCellCtxMenu = useCallback(() => setCellCtxMenu(null), []);

  const getCellDisplayValue = useCallback((columnId: string, item: SLBPosition): string => {
    const value = item[columnId as keyof SLBPosition];
    if (value === null || value === undefined) return '';
    const col = columns.find((c) => (getColumnId(c)) === columnId);
    if (col) return formatCellValue(col, value);
    return String(value);
  }, [columns]);

  const cellCtxMenuItems = useMemo((): ContextMenuItem[] => {
    if (!cellCtxMenu) return [];
    const { columnId, item } = cellCtxMenu;
    const colConfig = columnConfig.find((c) => c.id === columnId);
    const label = colConfig?.label ?? columnId;
    const displayValue = getCellDisplayValue(columnId, item);
    const truncatedValue = displayValue.length > 30 ? displayValue.slice(0, 30) + '...' : displayValue;

    // Multi-cell selection context menu
    const cellCount = selectedCellCount();
    if (cellCount > 1) {
      const cellSet = selectedCellsForCopy();
      const formatValue = (rowItem: SLBPosition, colId: string) => getCellDisplayValue(colId, rowItem);
      const getHeader = (colId: string) => columnConfig.find((c) => c.id === colId)?.label ?? colId;
      return [
        {
          label: `Copy ${cellCount} cells`,
          icon: <ClipboardDocumentIcon className="h-3.5 w-3.5" />,
          shortcut: `${MOD_KEY}+C`,
          onClick: () => {
            const text = buildSelectionText(cellSet, colIds, colIdToIndex, sortedDataRef.current, { formatValue });
            navigator.clipboard.writeText(text).then(() => addToast('success', `Copied ${cellCount} cells`)).catch(() => {});
          },
        },
        {
          label: 'Copy with headers',
          icon: <ClipboardDocumentListIcon className="h-3.5 w-3.5" />,
          shortcut: `${MOD_ALT_KEY}+C`,
          onClick: () => {
            const text = buildSelectionText(cellSet, colIds, colIdToIndex, sortedDataRef.current, { formatValue, withHeaders: true, getHeader });
            navigator.clipboard.writeText(text).then(() => addToast('success', `Copied ${cellCount} cells with headers`)).catch(() => {});
          },
        },
        {
          label: `Export ${cellCount} cells to CSV`,
          icon: <ArrowDownTrayIcon className="h-3.5 w-3.5" />,
          dividerBefore: true,
          onClick: () => {
            exportSelectionToCsv(
              cellSet, colIds, colIdToIndex, sortedDataRef.current,
              { formatValue, withHeaders: true, getHeader },
              makeCsvFilename('inventory'),
            );
            addToast('success', `Exported ${cellCount} cells to CSV`);
          },
        },
      ];
    }

    const items: ContextMenuItem[] = [
      {
        label: 'Copy',
        icon: <ClipboardDocumentIcon className="h-3.5 w-3.5" />,
        shortcut: `${MOD_KEY}+C`,
        onClick: () => { copyTsvToClipboard([[displayValue]]).then(() => addToast('success', 'Copied 1 cell')).catch(() => {}); },
      },
      {
        label: 'Copy with header',
        icon: <ClipboardDocumentListIcon className="h-3.5 w-3.5" />,
        shortcut: `${MOD_ALT_KEY}+C`,
        onClick: () => { copyTsvToClipboard([[label], [displayValue]]).then(() => addToast('success', 'Copied 1 cell with header')).catch(() => {}); },
      },
      {
        label: 'Copy entire row',
        icon: <TableCellsIcon className="h-3.5 w-3.5" />,
        onClick: () => {
          const visibleCols = columns.filter((c) => {
            const cid = getColumnId(c);
            return cid !== 'rowNumber';
          });
          const headers = visibleCols.map((c) => {
            const cid = getColumnId(c);
            const cfg = columnConfig.find((cc) => cc.id === cid);
            return cfg?.label ?? cid;
          });
          const values = visibleCols.map((c) => {
            const cid = getColumnId(c);
            return getCellDisplayValue(cid, item);
          });
          copyTsvToClipboard([headers, values]).then(() => addToast('success', `Copied row (${values.length} cells)`)).catch(() => {});
        },
      },
      {
        label: 'Export cell to CSV',
        icon: <ArrowDownTrayIcon className="h-3.5 w-3.5" />,
        dividerBefore: true,
        onClick: () => {
          const cellSet = selectedCellsForCopy();
          if (cellSet.size === 0) return;
          const formatValue = (rowItem: SLBPosition, colId: string) => getCellDisplayValue(colId, rowItem);
          const getHeader = (colId: string) => columnConfig.find((c) => c.id === colId)?.label ?? colId;
          exportSelectionToCsv(
            cellSet, colIds, colIdToIndex, sortedDataRef.current,
            { formatValue, withHeaders: true, getHeader },
            makeCsvFilename('inventory'),
          );
          addToast('success', 'Exported 1 cell to CSV');
        },
      },
    ];

    // Filter submenu for numeric columns
    const filterId = COLUMN_ID_TO_FILTER_ID[columnId];
    const conditionKey = filterId ? CONDITION_KEYS[filterId] : undefined;
    const rawValue = item[columnId as keyof SLBPosition];

    if (conditionKey && typeof rawValue === 'number') {
      items.push({
        label: `Filter ${truncatedValue} in ${label}`,
        icon: <FunnelIcon className="h-3.5 w-3.5" />,
        dividerBefore: true,
        submenu: [
          {
            label: `Equals ${truncatedValue}`,
            onClick: () => {
              setLocalInventoryFilters({
                ...localInventoryFilters,
                [conditionKey]: { operator: 'equals', value1: rawValue, value2: null } as NumericFilterCondition,
              });
            },
          },
          {
            label: `Less than ${truncatedValue}`,
            onClick: () => {
              setLocalInventoryFilters({
                ...localInventoryFilters,
                [conditionKey]: { operator: 'lessThan', value1: rawValue, value2: null } as NumericFilterCondition,
              });
            },
          },
          {
            label: `Greater than ${truncatedValue}`,
            onClick: () => {
              setLocalInventoryFilters({
                ...localInventoryFilters,
                [conditionKey]: { operator: 'greaterThan', value1: rawValue, value2: null } as NumericFilterCondition,
              });
            },
          },
          {
            label: `Not equal to ${truncatedValue}`,
            onClick: () => {
              setLocalInventoryFilters({
                ...localInventoryFilters,
                [conditionKey]: { operator: 'notEqual', value1: rawValue, value2: null } as NumericFilterCondition,
              });
            },
          },
        ],
      });
    }

    // "Filter by {value}" for multiselect columns (accountId, counterparty, currency, caStatus, etc.)
    const arrayKey = COLUMN_ID_TO_FILTER_ARRAY_KEY[columnId];
    if (arrayKey && displayValue) {
      const currentArr = localInventoryFilters[arrayKey] as string[];
      const isAlreadyFiltered = currentArr.includes(displayValue);
      items.push({
        label: isAlreadyFiltered ? `Remove filter ${truncatedValue}` : `Filter by ${truncatedValue}`,
        icon: <FunnelIcon className="h-3.5 w-3.5" />,
        dividerBefore: !conditionKey, // only add divider if no numeric filter above
        onClick: () => {
          setLocalInventoryFilters({
            ...localInventoryFilters,
            [arrayKey]: isAlreadyFiltered
              ? currentArr.filter((v) => v !== displayValue)
              : [...currentArr, displayValue],
          });
        },
      });
    }

    return items;
  }, [cellCtxMenu, selectedCellCount, selectedCellsForCopy, columnConfig, columns, getCellDisplayValue, localInventoryFilters, setLocalInventoryFilters, sortedDataRef, colIds, colIdToIndex, addToast]);

  // Close context menus on table scroll (caller passes ref)
  const bindScrollClose = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const handleScroll = () => {
      setHeaderCtxMenu(null);
      setCellCtxMenu(null);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  return {
    headerCtxMenu,
    headerCtxMenuItems,
    handleColumnContextMenu,
    closeHeaderCtxMenu,
    cellCtxMenu,
    cellCtxMenuItems,
    handleCellContextMenu,
    closeCellCtxMenu,
    bindScrollClose,
  };
}
